import { useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'
import { type Action, type Entity } from '#app/utils/permissions.ts'

function isUser(
	user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Access = 'own' | 'any' | 'own,any' | 'any,own'
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

export type UserForPermissionCheck = {
	roles: Array<{
		name: string
		permissions: Array<{
			entity: string
			action: string
			access: string
		}>
	}>
}

export function userHasPermission(
	user: UserForPermissionCheck | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some((role) =>
		role.permissions.some(
			(perm: { entity: string; action: string; access: string }) =>
				perm.entity === entity &&
				perm.action === action &&
				(!access || access.includes(perm.access)),
		),
	)
}

export function userHasAnyPermission(
	user: UserForPermissionCheck | null | undefined,
	permissions: PermissionString[],
) {
	if (!user) return false

	return permissions.some((permission) => userHasPermission(user, permission))
}

export function userHasAllPermissions(
	user: UserForPermissionCheck | null | undefined,
	permissions: PermissionString[],
) {
	if (!user) return false
	return permissions.every((permission) => userHasPermission(user, permission))
}

export function userHasRole(user: UserForPermissionCheck | null, role: string) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}
